import { Check, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackedDownloadUrl } from "@/lib/downloadTracking";
import goldstandards from "@/assets/goldstandards.webp";
import behandlung from "@/assets/behandlung.webp";
import fallNachMonat from "@/assets/fall-nach-monat.webp";
import fall2Monate from "@/assets/fall-2-monate.webp";
import zellen from "@/assets/zellen.webp";

const translations = {
  de: {
    badge1: "Das Original",
    title1: "Unschlagbares Original EAP® Abutment",
    description1: "Maßgeschneiderte Abutments, die eine optimale Funktionalität und Ästhetik gewährleisten. Entwickelt für höchste Ansprüche in der Implantologie.",
    benefits: [
      "Patientenindividuelles Hybrid-Abutment für optimale Passform",
      "Enorme Zeitersparnis & höchste Rot-Weiß-Ästhetik",
      "Nachträglich veränderbar in Minuten ohne Kronenwechsel",
      "Materialunabhängig - nutzen Sie Ihr gewohntes Kronenmaterial",
      "Geeignet für 3D gedruckte Kronen",
      "Ready für die Roboter-Implantologie",
    ],
    registerButton: "Jetzt registrieren",
    faqButton: "Häufige Fragen",
    badge2: "Perfektion im Detail",
    title2: "EAP®'n Sie schon?",
    description2a: "Aus unternehmerischer und medizinischer Perspektive zahlt sich der Umstieg aus! Patientenspezifische Emergenzprofile für natürlich aussehende Implantatversorgungen.",
    description2b: "Überzeugen Sie sich selbst von den Vorteilen der EAP®-Technologie und erleben Sie eine neue Dimension in der Implantatprothetik.",
    contactButton: "Kontakt aufnehmen",
    casesBadge: "Behandlungsfälle",
    casesTitle: "Klinische Fälle",
    casesSubtitle: "Erfolgreiche Behandlungsbeispiele mit EAP® Abutments",
    case1Title: "Zahn 24 nach Einheilphase",
    case1Desc: "Heilungsverlauf nach einem Monat mit EAP® Abutment",
    case2Title: "Zahn 46 nach Einheilphase",
    case2Desc: "Langzeitergebnis nach zwei Monaten Heilungsphase",
    case3Title: "Zellstrukturen",
    case3Desc: "Biologische Integration und Gewebeanpassung",
    brochureButton: "Weitere Fälle in unserer Broschüre",
  },
  en: {
    badge1: "The Original",
    title1: "Unbeatable Original EAP® Abutment",
    description1: "Custom abutments that ensure optimal functionality and aesthetics. Developed for the highest demands in implantology.",
    benefits: [
      "Patient-specific hybrid abutment for optimal fit",
      "Enormous time savings & highest red-white aesthetics",
      "Modifiable later in minutes without crown replacement",
      "Material independent - use your familiar crown material",
      "Suitable for 3D printed crowns",
      "Ready for robotic implantology",
    ],
    registerButton: "Register now",
    faqButton: "Frequently asked questions",
    badge2: "Perfection in detail",
    title2: "Are you already EAP®'ing?",
    description2a: "From an entrepreneurial and medical perspective, the switch pays off! Patient-specific emergence profiles for natural-looking implant restorations.",
    description2b: "Convince yourself of the advantages of EAP® technology and experience a new dimension in implant prosthetics.",
    contactButton: "Get in touch",
    casesBadge: "Treatment cases",
    casesTitle: "Clinical cases",
    casesSubtitle: "Successful treatment examples with EAP® Abutments",
    case1Title: "Tooth 24 after healing phase",
    case1Desc: "Healing progress after one month with EAP® Abutment",
    case2Title: "Tooth 46 after healing phase",
    case2Desc: "Long-term result after two months healing phase",
    case3Title: "Cell structures",
    case3Desc: "Biological integration and tissue adaptation",
    brochureButton: "More cases in our brochure",
  },
  fr: {
    badge1: "L'original",
    title1: "Pilier EAP® original imbattable",
    description1: "Piliers sur mesure garantissant une fonctionnalité et une esthétique optimales. Développé pour les plus hautes exigences en implantologie.",
    benefits: [
      "Pilier hybride spécifique au patient pour un ajustement optimal",
      "Énorme gain de temps et esthétique rouge-blanc optimale",
      "Modifiable ultérieurement en quelques minutes sans remplacement de couronne",
      "Indépendant du matériau - utilisez votre matériau de couronne habituel",
      "Convient aux couronnes imprimées en 3D",
      "Prêt pour l'implantologie robotique",
    ],
    registerButton: "S'inscrire maintenant",
    faqButton: "Questions fréquentes",
    badge2: "Perfection dans les détails",
    title2: "Utilisez-vous déjà EAP®?",
    description2a: "D'un point de vue entrepreneurial et médical, le changement en vaut la peine! Profils d'émergence spécifiques au patient pour des restaurations implantaires d'apparence naturelle.",
    description2b: "Convainquez-vous des avantages de la technologie EAP® et découvrez une nouvelle dimension en prothèse implantaire.",
    contactButton: "Prendre contact",
    casesBadge: "Cas de traitement",
    casesTitle: "Cas cliniques",
    casesSubtitle: "Exemples de traitement réussis avec les piliers EAP®",
    case1Title: "Dent 24 après phase de guérison",
    case1Desc: "Progression de la guérison après un mois avec le pilier EAP®",
    case2Title: "Dent 46 après phase de guérison",
    case2Desc: "Résultat à long terme après deux mois de phase de guérison",
    case3Title: "Structures cellulaires",
    case3Desc: "Intégration biologique et adaptation tissulaire",
    brochureButton: "Plus de cas dans notre brochure",
  },
  nl: {
    badge1: "Het origineel",
    title1: "Onverslaanbaar origineel EAP® Abutment",
    description1: "Aangepaste abutments die optimale functionaliteit en esthetiek garanderen. Ontwikkeld voor de hoogste eisen in de implantologie.",
    benefits: [
      "Patiëntspecifiek hybride abutment voor optimale pasvorm",
      "Enorme tijdsbesparing en hoogste rood-witte esthetiek",
      "Later aanpasbaar in minuten zonder kroonvervanging",
      "Materiaal onafhankelijk - gebruik uw vertrouwde kroonmateriaal",
      "Geschikt voor 3D-geprinte kronen",
      "Klaar voor robotische implantologie",
    ],
    registerButton: "Nu registreren",
    faqButton: "Veelgestelde vragen",
    badge2: "Perfectie in detail",
    title2: "Bent u al aan het EAP®'en?",
    description2a: "Vanuit ondernemend en medisch perspectief loont de overstap! Patiëntspecifieke emergentieprofielen voor natuurlijk ogende implantaatrestauraties.",
    description2b: "Overtuig uzelf van de voordelen van EAP® technologie en ervaar een nieuwe dimensie in implantaatprothetiek.",
    contactButton: "Neem contact op",
    casesBadge: "Behandelcases",
    casesTitle: "Klinische gevallen",
    casesSubtitle: "Succesvolle behandelvoorbeelden met EAP® Abutments",
    case1Title: "Tand 24 na genezingsfase",
    case1Desc: "Genezingsvoortgang na één maand met EAP® Abutment",
    case2Title: "Tand 46 na genezingsfase",
    case2Desc: "Langetermijnresultaat na twee maanden genezingsfase",
    case3Title: "Celstructuren",
    case3Desc: "Biologische integratie en weefselaanpassing",
    brochureButton: "Meer gevallen in onze brochure",
  },
  it: {
    badge1: "L'originale",
    title1: "Abutment EAP® originale imbattibile",
    description1: "Abutment personalizzati che garantiscono funzionalità ed estetica ottimali. Sviluppato per le più alte esigenze in implantologia.",
    benefits: [
      "Abutment ibrido specifico per il paziente per una vestibilità ottimale",
      "Enorme risparmio di tempo e massima estetica rosso-bianco",
      "Modificabile successivamente in pochi minuti senza sostituzione della corona",
      "Indipendente dal materiale - utilizzate il vostro materiale per corone abituale",
      "Adatto per corone stampate in 3D",
      "Pronto per l'implantologia robotica",
    ],
    registerButton: "Registrati ora",
    faqButton: "Domande frequenti",
    badge2: "Perfezione nei dettagli",
    title2: "Stai già facendo EAP®?",
    description2a: "Da un punto di vista imprenditoriale e medico, il passaggio vale la pena! Profili di emergenza specifici per il paziente per restauri implantari dall'aspetto naturale.",
    description2b: "Convincetevi dei vantaggi della tecnologia EAP® e sperimentate una nuova dimensione nella protesi implantare.",
    contactButton: "Mettiti in contatto",
    casesBadge: "Casi di trattamento",
    casesTitle: "Casi clinici",
    casesSubtitle: "Esempi di trattamento di successo con gli abutment EAP®",
    case1Title: "Dente 24 dopo fase di guarigione",
    case1Desc: "Progresso di guarigione dopo un mese con abutment EAP®",
    case2Title: "Dente 46 dopo fase di guarigione",
    case2Desc: "Risultato a lungo termine dopo due mesi di fase di guarigione",
    case3Title: "Strutture cellulari",
    case3Desc: "Integrazione biologica e adattamento tissutale",
    brochureButton: "Altri casi nella nostra brochure",
  },
  es: {
    badge1: "El original",
    title1: "Pilar EAP® original imbatible",
    description1: "Pilares personalizados que garantizan una funcionalidad y estética óptimas. Desarrollado para las más altas exigencias en implantología.",
    benefits: [
      "Pilar híbrido específico del paciente para un ajuste óptimo",
      "Enorme ahorro de tiempo y máxima estética rojo-blanco",
      "Modificable posteriormente en minutos sin reemplazo de corona",
      "Independiente del material - use su material de corona habitual",
      "Adecuado para coronas impresas en 3D",
      "Listo para la implantología robótica",
    ],
    registerButton: "Regístrese ahora",
    faqButton: "Preguntas frecuentes",
    badge2: "Perfección en el detalle",
    title2: "¿Ya está haciendo EAP®?",
    description2a: "Desde una perspectiva empresarial y médica, ¡el cambio vale la pena! Perfiles de emergencia específicos del paciente para restauraciones de implantes de aspecto natural.",
    description2b: "Convénzase de las ventajas de la tecnología EAP® y experimente una nueva dimensión en la prótesis de implantes.",
    contactButton: "Ponerse en contacto",
    casesBadge: "Casos de tratamiento",
    casesTitle: "Casos clínicos",
    casesSubtitle: "Ejemplos de tratamiento exitosos con pilares EAP®",
    case1Title: "Diente 24 después de la fase de curación",
    case1Desc: "Progreso de curación después de un mes con pilar EAP®",
    case2Title: "Diente 46 después de la fase de curación",
    case2Desc: "Resultado a largo plazo después de dos meses de fase de curación",
    case3Title: "Estructuras celulares",
    case3Desc: "Integración biológica y adaptación tisular",
    brochureButton: "Más casos en nuestro folleto",
  },
};

export const ProductShowcase = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <section id="produkt" className="py-12">
      <div className="container mx-auto overflow-hidden px-4 sm:overflow-visible">
        {/* First Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="animate-fade-up">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              {t.badge1}
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6 italic">
              {t.title1}
            </h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              {t.description1}
            </p>

            <div className="space-y-4 mb-8">
              {t.benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-primary" />
                  </div>
                  <span className="text-foreground">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
                onClick={() => window.location.href = 'https://shop.eap-abutments.com/login/signup'}
              >
                {t.registerButton}
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-primary/20 text-foreground hover:bg-primary/10"
                onClick={() => window.location.href = '/faq'}
              >
                {t.faqButton}
              </Button>
            </div>
          </div>

          <div className="relative animate-fade-in">
            <div className="relative rounded-2xl overflow-hidden shadow-card bg-secondary/20">
              <img 
                src={goldstandards}
                alt="Vergleich Goldstandards Abutments vs. EAP® Abutments - Vorteile patientenindividueller Hybrid-Abutments" 
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="absolute bottom-0 right-0 w-48 h-48 bg-primary/10 rounded-2xl -z-10" />
          </div>
        </div>

        {/* Second Section */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1 relative animate-fade-in">
            <div className="relative rounded-2xl overflow-hidden shadow-card bg-secondary/20">
              <img 
                src={behandlung}
                alt="Behandlungsablauf EAP® Abutments - Enorme Zeitersparnis und höchste Rot-Weiß-Ästhetik bei Zahnimplantaten" 
                className="w-full h-auto object-cover"
              />
            </div>
            <div className="absolute left-0 top-0 w-48 h-48 bg-primary/10 rounded-2xl -z-10" />
          </div>

          <div className="order-1 lg:order-2 animate-fade-up">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              {t.badge2}
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
              {t.title2}
            </h2>
            <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
              {t.description2a}
            </p>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              {t.description2b}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
                onClick={() => window.location.href = 'https://shop.eap-abutments.com/login/signup'}
              >
                {t.registerButton}
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-primary/20 text-foreground hover:bg-primary/10"
                onClick={() => window.location.href = '#kontakt'}
              >
                {t.contactButton}
              </Button>
            </div>
          </div>
        </div>

        {/* Case Studies Section */}
        <div className="mt-20">
          <div className="text-center mb-12 animate-fade-up">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              {t.casesBadge}
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {t.casesTitle}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t.casesSubtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="relative animate-fade-in">
              <div className="relative rounded-2xl overflow-hidden shadow-card bg-secondary/20">
                <img 
                  src={fallNachMonat}
                  alt="Klinischer Fall nach einem Monat - EAP® Abutment Behandlungsergebnis" 
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-semibold text-foreground mb-2">{t.case1Title}</h3>
                <p className="text-muted-foreground">{t.case1Desc}</p>
              </div>
            </div>

            <div className="relative animate-fade-in">
              <div className="relative rounded-2xl overflow-hidden shadow-card bg-secondary/20">
                <img 
                  src={fall2Monate}
                  alt="Klinischer Fall nach zwei Monaten - EAP® Abutment Langzeitergebnis" 
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-semibold text-foreground mb-2">{t.case2Title}</h3>
                <p className="text-muted-foreground">{t.case2Desc}</p>
              </div>
            </div>

            <div className="relative animate-fade-in">
              <div className="relative rounded-2xl overflow-hidden shadow-card bg-secondary/20">
                <img 
                  src={zellen}
                  alt="Zellstrukturen und Gewebeanpassung bei EAP® Abutments - Biologische Integration" 
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-semibold text-foreground mb-2">{t.case3Title}</h3>
                <p className="text-muted-foreground">{t.case3Desc}</p>
              </div>
            </div>
          </div>

          {/* Broschüren Button */}
          <div className="text-center mt-12 animate-fade-up">
            <Button 
              size="lg"
              variant="outline"
              className="h-auto max-w-full whitespace-normal border-2 border-primary px-4 py-4 text-base leading-snug text-primary hover:bg-primary hover:text-primary-foreground sm:px-8 sm:py-6 sm:text-lg"
              asChild
            >
              <a href={trackedDownloadUrl("broschuere-2025", language)}>
                <FileText className="mr-2 h-5 w-5 shrink-0 sm:mr-3 sm:h-6 sm:w-6" />
                {t.brochureButton}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};